<template>
  <aside class="w-full h-full flex flex-col px-6 md:px-0">

    <!-- Advertisements -->
    <section class="flex-1 bg-white/80 backdrop-blur-md p-6 shadow-xl rounded-2xl border border-gray-100 transition-all duration-300 hover:shadow-2xl relative overflow-y-auto overflow-x-hidden scrollbar-hide max-h-[820px] group">
      <!-- Decorative background glow -->
      <div class="absolute -top-10 -left-10 w-32 h-32 bg-[#FFE9A0]/40 rounded-full blur-2xl opacity-50 z-0 pointer-events-none"></div>

      <div class="relative z-10 flex items-center mb-6 space-x-2">
        <h2 class="text-xl font-bold text-transparent bg-clip-text bg-gradient-to-r from-[#D84040] to-red-500">
          Advertisements
        </h2>
        <span class="text-gray-400 font-medium text-sm">( বিজ্ঞাপন )</span>
      </div>

      <div class="space-y-8 relative z-10">
        <!-- Static Publish Section -->
        <div class="relative group/ad">
          <h3 class="text-base font-semibold mb-3 text-gray-800 flex flex-col">
            Publish Your Advertisement
            <span class="text-xs text-gray-500 mt-1 uppercase tracking-wider">আপোনাৰ বিজ্ঞাপন প্ৰকাশ কৰক</span>
          </h3>
          <div class="relative w-full rounded-xl overflow-hidden shadow-sm group-hover/ad:shadow-md transition-shadow">
            <a href="#" @click.prevent="openPublishModal" class="block">
              <img
                :src="publishImage"
                alt="Publish Ad"
                class="w-full h-40 md:h-48 object-cover transition-transform duration-700 ease-in-out group-hover/ad:scale-105"
              />
              <div class="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover/ad:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
            </a>
          </div>
          <p class="text-gray-600 text-sm mt-4 text-center leading-relaxed">
            Want to showcase your film or event? Publish your advertisement
            with us and reach a wider audience.
          </p>
          <div class="flex justify-center mt-4">
            <button
              @click="openPublishModal"
              class="bg-gradient-to-r from-[#FFE9A0] to-[#FFD580] text-gray-800 font-medium px-6 py-2 shadow-sm hover:shadow transition-all rounded-full hover:-translate-y-0.5"
            >
              Publish
            </button>
          </div>
        </div>

        <!-- Dynamic Ads -->
        <div v-for="ad in ads" :key="ad.id" class="border-t border-gray-100 pt-6 relative group/dynad">
          <h3 class="text-lg font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-gray-700 to-gray-900">
            {{ ad.title }}
          </h3>
          <div class="relative w-full rounded-xl overflow-hidden shadow-sm group-hover/dynad:shadow-md transition-shadow">
            <img
              :src="ad.image"
              :alt="ad.title"
              class="w-full h-40 md:h-48 object-cover transition-transform duration-700 ease-in-out group-hover/dynad:scale-105"
              @error="handleImageError"
            />
            <div class="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent opacity-0 group-hover/dynad:opacity-100 transition-opacity duration-300 pointer-events-none"></div>
          </div>
          <p class="text-gray-600 text-[15px] mt-4 text-center leading-relaxed">
            {{ ad.description }}
          </p>
          <p class="text-gray-400 font-medium text-xs mt-2 text-center flex items-center justify-center space-x-1">
            <svg class="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path></svg>
            <span>{{ formatDate(ad.publish_date) }}</span>
          </p>
        </div>
      </div>

      <!-- Ads Error -->
      <p v-if="adsError" class="text-red-500 font-medium text-sm mt-4 text-center bg-red-50 p-3 rounded-lg border border-red-100">
        {{ adsError }}
      </p>
    </section>

    <!-- Advertisement Section -->
    <AdvertisementForm v-if="showAdForm" @close="showAdForm = false" />
  </aside>
</template>

<script setup>
import { ref, onMounted } from "vue";
import Swal from 'sweetalert2';
import publishImage from "@/assets/images/publish.jpg";
import AdvertisementForm from './AdvertisementForm.vue';
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
gsap.registerPlugin(ScrollTrigger);

const API_BASE_URL = process.env.VUE_APP_API_BASE_URL;
const FILE_BASE_URL = process.env.VUE_APP_FILE_BASE_URL;

if (!API_BASE_URL || !FILE_BASE_URL) {
  console.error(
    "Missing .env variables! Add VUE_APP_API_BASE_URL and VUE_APP_FILE_BASE_URL"
  );
}

const ads = ref([]);
const adsError = ref("");
const showAdForm = ref(false);

const resolveImage = (img) => {
  if (!img) return `${FILE_BASE_URL}/images/default.png`;
  if (typeof img === "string" && img.startsWith("data:")) return img;
  if (typeof img === "string" && (img.startsWith("http://") || img.startsWith("https://"))) return img;
  if (img instanceof Blob) return URL.createObjectURL(img);
  if (typeof img === "string") return `${FILE_BASE_URL}${img}`;
  return `${FILE_BASE_URL}/images/default.png`;
};

const fetchAds = async () => {
  const url = `${API_BASE_URL}/advertisments`;
  try {
    const res = await fetch(url);
    if (!res.ok) throw new Error(`HTTP ${res.status}`);
    const data = await res.json();
    ads.value = data.map((ad) => ({
      ...ad,
      image: resolveImage(ad.image),
    }));
  } catch (err) {
    adsError.value = "Failed to load ads.";
    console.error("fetchAds error:", err);
  }
};

const handleImageError = (e) => {
  const img = e.target;
  if (img.src.includes("default.png")) {
    img.src = "data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMzAwIiBoZWlnaHQ9IjIwMCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48cmVjdCB3aWR0aD0iMTAwJSIgaGVpZ2h0PSIxMDAlIiBmaWxsPSIjZGRkIi8+PHRleHQgeD0iNTAlIiB5PSI1MCUiIGZvbnQtc2l6ZT0iMjQiIHRleHQtYW5jaG9yPSJtaWRkbGUiIGR5PSIuM2VtIiBmaWxsPSIjOTk5Ij5ObyBJbWFnZTwvdGV4dD48L3N2Zz4=";
    return;
  }
  img.src = `${FILE_BASE_URL}/images/default.png`;
  img.onerror = null;
};

const formatDate = (dateStr) => {
  if (!dateStr) return "N/A";
  return new Date(dateStr).toLocaleDateString("en-GB", {
    day: "numeric",
    month: "short",
    year: "numeric",
  });
};

const openPublishModal = () => {
  const user = JSON.parse(localStorage.getItem('user') || 'null');
  const token = localStorage.getItem('token');

  if (!user || !token) {
    Swal.fire({
      icon: 'warning',
      title: 'Login Required',
      text: 'Please log in to publish an advertisement.',
      confirmButtonColor: '#BF3131',
      showCancelButton: true,
      confirmButtonText: 'Go to Login',
      cancelButtonText: 'Cancel'
    }).then(result => {
      if (result.isConfirmed) window.location.href = '/login';
    });
    return;
  }
  showAdForm.value = true;
};

onMounted(async () => {
  await fetchAds();
  gsap.from(".gsap-ad", {
    scrollTrigger: {
      trigger: ".gsap-ad",
      start: "top 90%",
      toggleActions: "play none none none",
    },
    opacity: 0,
    y: 40,
    duration: 1,
    stagger: 0.25,
    ease: "power3.out",
  });
});
</script>

<style scoped>
.scrollbar-hide {
  -ms-overflow-style: none;
  scrollbar-width: none;
}
.scrollbar-hide::-webkit-scrollbar {
  display: none;
}
</style>